import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { MAIN_SITE_URL, categories } from "@/mocks/categories";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const anchorLinks = [
    { key: "nav.spreadsheet", href: "#categories" },
    { key: "nav.guides", href: "#guides" },
    { key: "nav.faq", href: "#faq" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50 border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to={`/${i18n.language}`} className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-white text-lg"></i>
            </span>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              USFans<span className="text-primary-600">Sheet</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to={`/${i18n.language}`}
              className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("nav.home")}
            </Link>
            <Link
              to={`/${i18n.language}/article/usfans-buying-guide`}
              className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("nav.buyingGuide")}
            </Link>
            {anchorLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t(link.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("hero.ctaSecondary")}
            </a>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-10 h-10 rounded-full flex items-center justify-center text-foreground-900 hover:bg-background-100 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-background-50 overflow-y-auto border-t border-background-200 px-4 py-4">
          <nav className="flex flex-col gap-1">
            <Link
              to={`/${i18n.language}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-5-line text-lg"></i>
              </span>
              {t("nav.home")}
            </Link>
            <Link
              to={`/${i18n.language}/article/usfans-buying-guide`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-shopping-cart-2-line text-lg"></i>
              </span>
              {t("nav.buyingGuide")}
            </Link>
            {anchorLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-arrow-right-line text-lg"></i>
                </span>
                {t(link.key)}
              </a>
            ))}
          </nav>

          {/* Categories */}
          <div className="mt-5 pt-5 border-t border-background-200">
            <h4 className="px-4 text-xs font-bold uppercase tracking-wider text-foreground-500 mb-2">
              {t("mobile.categories")}
            </h4>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-accent-500 shrink-0">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  <span className="truncate">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-5 pt-5 border-t border-background-200 flex flex-col gap-2">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t("hero.ctaSecondary")}
            </a>
            <div className="flex justify-center">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}